import {
    ChangeDetectorRef,
    Injector,
    OnDestroy,
    ViewChild,
} from '@angular/core';
import { AppStateFacade } from '@app-state';
import { UiLicenseDashboardComponent } from '@components/shared/license/ui-license-dashboard.component';
import {
    IHostLicense,
    ILicense,
    ILicenseOfflineRequest,
    ILicenseRequest,
    LicenseServerErrorCode,
    LicenseType,
} from '@models';
import { FileDownloadService } from '@services/file.download.service';
import { getProperty } from '@utils/object.utils';

import {
    forkJoin,
    Observable,
    of,
    Subject,
} from 'rxjs';
import {
    catchError,
    filter,
    switchMap,
} from 'rxjs/operators';

export class BaseLicensePage<T extends ILicense | IHostLicense> implements OnDestroy {
    public license$: Observable<T>;
    public LicenseServerErrorCode = LicenseServerErrorCode;

    @ViewChild(UiLicenseDashboardComponent, { static: false })
    protected _dashboardComponent!: UiLicenseDashboardComponent;

    protected _destroyed$ = new Subject();

    constructor(
        protected _appState: AppStateFacade,
        protected _downloadService: FileDownloadService,
        protected _injector: Injector,
        protected _cd: ChangeDetectorRef,
    ) {
        this.license$ = this._appState.user.license.value$ as Observable<T>;
    }

    ngOnDestroy() {
        this._destroyed$.next();
    }

    public onUpload(request: ILicenseOfflineRequest) {
        const upload$ = request.type === LicenseType.Legacy ?
            this._appState.user.license.uploadLicense(request.file) :
            this._appState.user.license.activateLicenseOffline(request.file);

        upload$
            .pipe(
                catchError(error => this._handleLicenseInvalid(error)),
            )
            .subscribe();
    }

    public onSubmit(request: ILicenseOfflineRequest) {
        this.onUpload(request);
    }

    public onOnlineActivate(request: ILicenseRequest) {
        this._appState.user.license.activateLicenseOnline(request.licenseKey, request.environment)
            .pipe(
                catchError(error => {
                    // No internet connection
                    if (error.status === 503 &&
                        getProperty(error, 'error.errorCode') === LicenseServerErrorCode.ServerConnectionFailure) {
                        this.openActivateOfflineModal(true);
                    }
                    return this._handleLicenseInvalid(error);
                }),
            )
            .subscribe();
    }

    public onOfflineActivate(request: ILicenseRequest) {
        this._appState.user.license
            .getLicenseOffline(request.licenseKey, request.environment)
            .subscribe(this._saveFileAs('activation_request.txt'));
    }

    public onOfflineDeactivate() {
        this._appState.user.license
            .getDeactivateLicenseOffline()
            .subscribe(this._saveFileAs('deactivation_request.txt'));
    }

    public onOnlineUpdate() {
        this._appState.user.license.updateLicenseOnline()
            .pipe(
                catchError(error => {
                    // No internet connection
                    if (error.status === 503 &&
                        getProperty(error, 'error.errorCode') === LicenseServerErrorCode.ServerConnectionFailure) {
                        this.openUpdateModal(true);
                    }
                    return this._handleLicenseInvalid(error);
                }),
            )
            .subscribe();
    }

    public openActivateOfflineModal(offline: boolean = false) {
        this._dashboardComponent.openActivateOfflineModal(offline);
    }

    public openUpdateModal(failed: boolean = false) {
        this._dashboardComponent.openUpdateModal(failed);
    }

    public openDeactivateOfflineModal(tenantId?: number) {
        this._dashboardComponent.openDeactivateOfflineModal(tenantId);
    }

    protected _onRemove(deactivate: boolean) {
        const worker$ = deactivate ?
            this._appState.user.license.deactivateLicenseOnline() : of({});

        worker$.pipe(
            catchError(error => {
                // No internet connection
                if (error.status === 503 &&
                    getProperty(error, 'error.errorCode') === LicenseServerErrorCode.ServerConnectionFailure) {
                    this.openDeactivateOfflineModal();
                }
                return of(error);
            }),
            filter(data => !data || !data.status || data.status !== 503),
            switchMap(() => this._appState.user.license.deleteLicense()),
        ).subscribe();
    }

    private _handleLicenseInvalid(error: any) {
        const errorCodes = [LicenseServerErrorCode.NotLicensed, LicenseServerErrorCode.AfterEndDate];
        if (
            error.status === 400 &&
            errorCodes.includes(getProperty(error, 'error.errorCode'))
        ) {
            return forkJoin([
                this._appState.user.license.deleteLicense(),
                of(error),
            ]);
        }

        return of(error);
    }

    private _saveFileAs = (name: string) => (result: { value: string } | null) => {
        if (result) {
            this._downloadService.downloadStringAsFile(name, 'text/plain', result.value);
        }
    }
}
